import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotification } from '../components/NotificationProvider';
import api from '../services/apiService';
import '../scss/register.scss';

function ParentRegister() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    relationship: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [teamInfo, setTeamInfo] = useState(null);
  const [childInfo, setChildInfo] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    // Get parameters from URL
    const params = new URLSearchParams(location.search);
    const teamId = params.get('t');
    const playerId = params.get('p');

    if (!teamId || !playerId) {
      showError('Invalid invitation link. Missing required parameters.');
      return;
    }

    // Fetch team and child information
    fetchInvitationInfo(teamId, playerId);
  }, [location]);

  const fetchInvitationInfo = async (teamId, playerId) => {
    try {
      // Fetch invitation information using single endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL}/register/get_invitation_info.php?t=${teamId}&p=${playerId}`);
      const data = await response.json();
      
      if (data.success) {
        setTeamInfo(data.team);
        setChildInfo(data.player);
      } else {
        console.error('Invitation API failed:', data);
        showError(data.message || 'Unable to load invitation details. Please check your link.');
      }
    } catch (error) {
      console.error('Error fetching invitation info:', error);
      showError('Unable to load invitation details. Please check your link.');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.relationship.trim()) {
      newErrors.relationship = 'Relationship to child is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhoneNumber(formData.phone)) {
      newErrors.phone = 'Please enter a valid UK mobile number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePhoneNumber = (phone) => {
    // Basic UK mobile number validation
    const phoneRegex = /^(\+44|0)7\d{9}$/;
    return phoneRegex.test(phone);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const params = new URLSearchParams(location.search);
      const teamId = params.get('t');
      const playerId = params.get('p');

      // Use fetch instead of api service to avoid interceptor redirects
      const response = await fetch(`${process.env.REACT_APP_API_URL}/register_parent.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          team_id: teamId,
          player_id: playerId
        })
      });

      const data = await response.json();

      if (data.success) {
        setRegistrationSuccess(true);
      } else {
        showError(data.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      showError('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!teamInfo || !childInfo) {
    return (
      <div className="register-page">
        <div className="container">
          <div className="register-form">
            <div className="loading">Loading invitation details...</div>
          </div>
        </div>
      </div>
    );
  }

  // Show thank you screen if registration was successful
  if (registrationSuccess) {
    return (
      <div className="register-page">
        <div className="container">
          <div className="register-form">
            <div className="thank-you-screen">
              <h1>Registration Successful!</h1>
              <p className="thank-you-message">
                Thank you for registering, {formData.firstName}! Your account has been created successfully.
              </p>
              <div className="approval-notice">
                <h3>What happens next?</h3>
                <p>Your registration is now pending coach approval. You will receive a notification once your account has been approved.</p>
                <p>Once approved, you'll be able to:</p>
                <ul>
                  <li>Follow {childInfo.first_name}'s football journey</li>
                  <li>Receive match updates and notifications</li>
                  <li>View team information and schedules</li>
                  <li>Access the full platform features</li>
                </ul>
              </div>
              <div className="contact-info">
                <p>If you have any questions, please contact your team's coach or manager.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="register-page">
      <div className="container">
        <div className="register-form">
          <div className="form-header">
            <h1>Parent Registration</h1>
            <p>You're being invited to follow {childInfo.first_name}'s football journey with {teamInfo.team_name}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={errors.firstName ? 'error' : ''}
                placeholder="Enter your first name"
              />
              {errors.firstName && <span className="error-message">{errors.firstName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={errors.lastName ? 'error' : ''}
                placeholder="Enter your last name"
              />
              {errors.lastName && <span className="error-message">{errors.lastName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="relationship">Relationship to Child *</label>
              <select
                id="relationship"
                name="relationship"
                value={formData.relationship}
                onChange={handleInputChange}
                className={errors.relationship ? 'error' : ''}
              >
                <option value="">Select relationship</option>
                <option value="Mother">Mother</option>
                <option value="Father">Father</option>
                <option value="Step-mother">Step-mother</option>
                <option value="Step-father">Step-father</option>
                <option value="Guardian">Guardian</option>
                <option value="Grandmother">Grandmother</option>
                <option value="Grandfather">Grandfather</option>
                <option value="Aunt">Aunt</option>
                <option value="Uncle">Uncle</option>
                <option value="Other">Other</option>
              </select>
              {errors.relationship && <span className="error-message">{errors.relationship}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="phone">Mobile Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={errors.phone ? 'error' : ''}
                placeholder="07xxx xxxxxx"
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? 'error' : ''}
                placeholder="Enter your email address"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={errors.password ? 'error' : ''}
                placeholder="Create a password (min 6 characters)"
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={errors.confirmPassword ? 'error' : ''}
                placeholder="Confirm your password"
              />
              {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
            </div>

            <button 
              type="submit" 
              className="submit-btn" 
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="form-footer">
            <p>Already have an account? <a href="/login">Log in here</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ParentRegister; 